import React, { useState, useEffect } from "react";
import { useTenancy } from "@/contexts/TenancyContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { FrameworkCombobox, FrameworksProvider } from "@/components/ui/FrameworkCombobox";


interface Framework {
  id?: string;
  standard: string;
  controlID?: string;
  controlName?: string;
  controlDescription?: string;
  framework?: string;
  controlApplicable?: boolean;
}

interface Party {
  id?: string | number;
  party: string; // Party name
  requirement: string;
  requirementsAddressedThroughISMS: string;
  frameworks: Framework[];
  dateAdded?: string | { seconds: number; nanoseconds: number };
  dateUpdated?: string | { seconds: number; nanoseconds: number };
  updatedBy?: string;
} // Now matches the table fields in Context/InterestedParties.tsx

// No longer needed: partyTypes, partyDetailTypes, or old Party.details structure.

interface InterestedPartiesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  party: Party | null;
  onSave: (party: Party) => void;
  isReadOnly?: boolean;
}

// Transform Firestore Timestamp to date string
const transformDate = (date: any) => {
  if (!date) return '';
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString('en-AU');
  }
  return date;
};

export default function InterestedPartiesDialog({
  open,
  onOpenChange,
  party,
  onSave,
  isReadOnly = false
}: InterestedPartiesDialogProps) {
  const { userIdToName } = useTenancy();
  // Default structure matches Context/InterestedParties.tsx
  const [currentParty, setCurrentParty] = useState<Party>({
    party: '',
    requirement: '',
    requirementsAddressedThroughISMS: '',
    frameworks: [],
    dateAdded: '',
    dateUpdated: '',
    updatedBy: '',
  });

  // Removed individual state for new items as they're now handled by comboboxes

  // Update local state when party prop changes
  useEffect(() => {
    if (party) {
      console.log('[InterestedPartiesDialog] party:', party);
      console.log('[InterestedPartiesDialog] party frameworks:', party?.frameworks ? party.frameworks : "No frameworks");
      setCurrentParty({
        ...party,
        party: party.party || '',
        requirement: party.requirement || '',
        requirementsAddressedThroughISMS: party.requirementsAddressedThroughISMS || '',
        frameworks: (party.frameworks || []).map(f => {
          // If it's already an object with an id, use it as is
          if (typeof f === 'object' && f !== null) {
            return {
              ...f,
              id: f.id || f.standard || f.framework || `fw-${Date.now()}`,
              standard: f.standard || f.framework || 'Unknown',
            };
          }
          // If it's a string, use it as both id and standard
          if (typeof f === 'string') {
            return {
              id: f,
              standard: f,
              name: f // Add name for better display
            };
          }
          // Fallback for any other case
          return {
            id: `fw-${Date.now()}`,
            standard: 'Unknown',
            name: 'Unknown'
          };
        }),
        dateAdded: party.dateAdded, // Keep original value for saving
        dateUpdated: party.dateUpdated, // Keep original value for saving
        updatedBy: party.updatedBy || '',
      });
    } else {
      setCurrentParty({
        party: '',
        requirement: '',
        requirementsAddressedThroughISMS: '',
        frameworks: [],
        dateAdded: '',
        dateUpdated: '',
        updatedBy: '',
      });
    }
  }, [party, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(currentParty);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto bg-secondary">
        <DialogHeader>
          <DialogTitle>{isReadOnly ? 'View' : (party?.id ? 'Edit' : 'Add New')} Party</DialogTitle>
          <DialogDescription>
            {isReadOnly ? 'View the party details below.' : (party?.id ? 'Update the party details below.' : 'Fill in the details for the new party.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Party</Label>
            <Input
              value={currentParty.party}
              onChange={e => !isReadOnly && setCurrentParty(prev => ({ ...prev, party: e.target.value }))}
              placeholder="Enter party name"
              readOnly={isReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label>Party Requirement</Label>
            <Textarea
              value={currentParty.requirement}
              onChange={e => !isReadOnly && setCurrentParty(prev => ({ ...prev, requirement: e.target.value }))}
              placeholder="Enter party requirement"
              rows={2}
              readOnly={isReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label>Outcome on Management System</Label>
            <Textarea
              value={currentParty.requirementsAddressedThroughISMS}
              onChange={e => !isReadOnly && setCurrentParty(prev => ({ ...prev, requirementsAddressedThroughISMS: e.target.value }))}
              placeholder="How is this requirement addressed?"
              rows={2}
              readOnly={isReadOnly}
            />
          </div>

          {/* <div className="space-y-2">
            <Label>Date Added</Label>
            <Input
              value={typeof currentParty.dateAdded === 'object' && currentParty.dateAdded && 'seconds' in currentParty.dateAdded ? transformDate(currentParty.dateAdded) : (currentParty.dateAdded || '')}
              onChange={e => setCurrentParty(prev => ({ ...prev, dateAdded: e.target.value }))}
              placeholder="Date added"
              type="text"
              disabled
            />
          </div>

          <div className="space-y-2">
            <Label>Date Updated</Label>
            <Input
              value={typeof currentParty.dateUpdated === 'object' && currentParty.dateUpdated && 'seconds' in currentParty.dateUpdated ? transformDate(currentParty.dateUpdated) : (currentParty.dateUpdated || '')}
              onChange={e => setCurrentParty(prev => ({ ...prev, dateUpdated: e.target.value }))}
              placeholder="Date updated"
              type="text"
              disabled
            />
          </div> */}

          {/* <div className="space-y-2">
            <Label>Updated By</Label>
            <Input
              value={isReadOnly && currentParty.updatedBy && userIdToName ? 
                userIdToName[currentParty.updatedBy] || currentParty.updatedBy : 
                currentParty.updatedBy || ''}
              onChange={e => setCurrentParty(prev => ({ ...prev, updatedBy: e.target.value }))}
              placeholder="User ID or Name"
              type="text"
              disabled
            />
          </div> */}
          <div className="space-y-2">
            <Label>Frameworks</Label>
            <FrameworksProvider>
              {isReadOnly ? (
                <div className="text-sm text-muted-foreground py-2">
                  {currentParty.frameworks.length > 0
                    ? currentParty.frameworks.map((f, idx) =>
                      <div key={f.id || idx} className="mb-1">
                        {f.standard || f.framework || 'Unknown Framework'}
                      </div>
                    )
                    : 'No frameworks assigned'}
                </div>
              ) : (
                <FrameworkCombobox
                  selected={currentParty.frameworks.map(f => f.id).filter(Boolean) as string[]}
                  setSelected={ids => setCurrentParty(prev => ({
                    ...prev,
                    frameworks: ids.map(id => prev.frameworks.find(f => f.id === id) || { id, standard: id })
                  }))}
                  placeholder="Select frameworks..."
                  className="w-full"
                />
              )}
            </FrameworksProvider>
            {/* <div className="flex flex-wrap gap-2 mt-2">
              {currentParty.frameworks.map((framework) => (
                <Badge
                  key={framework.id}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {framework.standard}
                  <button
                    type="button"
                    onClick={() => setCurrentParty(prev => ({
                      ...prev,
                      frameworks: prev.frameworks.filter(f => f.id !== framework.id)
                    }))}
                    className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div> */}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {!isReadOnly && <Button type="submit">{party?.id ? 'Save' : 'Add'}</Button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}